import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import QRCode from 'react-qr-code';
import { Delete, Edit, QrCode } from '@mui/icons-material';
import { ColumnContainer } from '../../components/CardTable/styles';
import { Modal } from '../../components/Modal/Modal';
import { ModalV2 } from '../../components/Modal/ModalV2';
import { FormEmployeeEdit } from './FormEmployeesEdit';
import { useToggle } from '../../hooks/useToggle';
import { PERMISSIONS } from '../../router/permissions';
import {
  deleteEmployeeMode, editEmployeeMode, employeeSelected, resetEmployeeSelected
} from '../../redux/actions/employeesCatalog/employeesCatalogActions';
import { safeRemoveChild } from "helpers/safeRemoveChild";

const style = {
  color: '#004F9F',
  fontSize: 20,
  marginRight: 10,
  cursor: 'pointer'
};

export const columns = [
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>No. Empleado</span>),
    accessor: "number",
    filterable: true,
    filterMethod: (filter, row) => row.number?.toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.number}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>Nombre</span>),
    accessor: "name",
    filterable: true,
    filterMethod: (filter, row) => row.name?.toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.name}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>A. Paterno</span>),
    accessor: "lastName",
    filterable: true,
    filterMethod: (filter, row) => row.lastName?.toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.lastName}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>A. Materno</span>),
    accessor: "secondName",
    filterable: true,
    filterMethod: (filter, row) => row.secondName?.toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.secondName}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>Departamento</span>),
    accessor: "department",
    filterable: true,
    filterMethod: (filter, row) => row.department?.toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.department}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>Puesto</span>),
    accessor: "job",
    filterable: true,
    filterMethod: (filter, row) => row.job?.toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.job}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>Contrato</span>),
    accessor: "contract",
    filterable: true,
    filterMethod: (filter, row) => row.contract?.toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.contract}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}># Licencia</span>),
    accessor: "licenseNumber",
    filterable: true,
    filterMethod: (filter, row) => row.licenseNumber?.toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.licenseNumber}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>Estatus</span>),
    accessor: "statusLabel",
    filterable: true,
    filterMethod: (filter, row) => String(row.statusLabel)?.toLowerCase().includes(filter.value.toLowerCase()),
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>RFC</span>),
    accessor: "rfc",
    filterable: true,
    filterMethod: (filter, row) => row.rfc?.toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.rfc}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>Teléfono</span>),
    accessor: "phone",
    filterable: true,
    filterMethod: (filter, row) => row.phone?.toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.phone}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>E-mail</span>),
    accessor: "email",
    filterable: true,
    filterMethod: (filter, row) => row.email?.toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.email}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>Dirección</span>),
    accessor: 'fullAddress',
    filterable: true,
    filterMethod: (filter, row) => row.fullAddress?.toLowerCase().includes(filter.value.toLowerCase()) || "",
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.fullAddress}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>Unidad</span>),
    accessor: 'unitNumber',
    filterable: true,
    filterMethod: (filter, row) => (row.unitNumber ?? "").toLowerCase().includes(filter.value.toLowerCase()),
    Cell: ({ original }) => (<span style={{ fontSize: '.8rem' }}>{original.unitNumber}</span>)
  },
  {
    Header: () => (<span style={{ fontSize: '.82rem' }}>Acciones</span>),
    width: 100,
    filterable: false,
    sortable: false,
    Cell: ({ original }) => {
      const dispatch = useDispatch();
      const qrRef = useRef(null);
      const { isLoadingComp } = useSelector(({ search }) => search);
      const employeeSelector = useSelector(state => state.employeesCatalog);
      const [open, setOpen] = useToggle();
      const [QRModal, setQRModal] = useState(false);

      const handleClose = () => {
        dispatch(resetEmployeeSelected());
        setOpen();
      };

      const openEdit = () => {
        dispatch(editEmployeeMode());
        dispatch(employeeSelected(original.id));
        setOpen();
      };
      const openEliminate = () => {
        dispatch(deleteEmployeeMode());
        dispatch(employeeSelected(original.id));
        setOpen();
      };
      const openQR = () => {
        dispatch(employeeSelected(original.id));
        setQRModal(true);
      };

      const downloadQR = () => {
        const svg = qrRef.current.querySelector("svg");
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svg);

        // Convert SVG string → image → canvas → PNG
        const canvas = document.createElement("canvas");
        const img = new Image();
        const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(svgBlob);

        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(url);

          // Create link & download
          const pngUrl = canvas.toDataURL("image/png");
          const downloadLink = document.createElement("a");
          downloadLink.href = pngUrl;
          downloadLink.download = "qr-code.png";
          document.body.appendChild(downloadLink);
          downloadLink.click();
          safeRemoveChild(downloadLink);
        };

        img.src = url;
      };

      return (
        <>
          <ColumnContainer style={{
            display: PERMISSIONS.PREPAYROLL_ADMIN ? 'flex' : 'none'
          }}>
            <Edit style={style} onClick={openEdit} />
            <Delete style={{ ...style, color: '#e83737' }} onClick={openEliminate} />
            <QrCode style={style} onClick={openQR} />
          </ColumnContainer>
          <ModalV2
            open={open}
            onClose={handleClose}
            toRight
            isLoading={isLoadingComp?.loadingEmployeesCrud}
          >
            <FormEmployeeEdit handleClose={setOpen} />
          </ModalV2>
          <Modal
            isOpen={QRModal}
            handleClose={() => setQRModal(false)}
            textButton2={"Descargar"}
            textButton1={"Cerrar"}
            actionFunction1={downloadQR}
            actionFunction2={() => setQRModal(false)}
            children={
              employeeSelector.employeeSelected && employeeSelector.employeeSelected.id && <div ref={qrRef}>
                <QRCode
                  value={employeeSelector.employeeSelected?.id}
                  size={256}
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              </div>
            }
          />
        </>
      )
    },
  }
];